import { Popover, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

//STYLE
import { TituloPopover } from './styled';

//APP
import { STATUS_COLETADA } from '../../meta';

//COMPONENTS
import ButtonCustom from '../../../../../../../../Components/ButtonCustom';
import { Box } from '../../../../../../../../Components/CustomUi/box';
import { OrdemCampo } from './components/ordemCampo';
import { OrdemLaboratorio } from './components/ordemLaboratorio';

//PROPS
interface Props {
    /** cod da OS da linha. */
    cod: number
    /** Status da OS — só COLETADA libera a ordem de laboratório. */
    status: number
}

/**
 * Ação "Imprimir ordem de coleta" da coluna Ações.
 *
 * Daqui são só o popover e o botão da coluna: cada tipo de ordem é um recurso
 * autocontido (botão + gaveta + folha) em `components/`, e o clique é tratado
 * dentro do próprio filho. Nada é compartilhado entre Campo e Laboratório de
 * propósito, para as regras de um não entrarem por `if/else` no outro.
 */
export const ImprimirOrdem: React.FC<Props> = ({ cod, status }) => {

    //#region STATES
    const [abrirPopover, setAbrirPopover] = useState(false)
    /**
     * Qual ordem está imprimindo, para uma desabilitar a outra.
     *
     * Único estado que os dois filhos compartilham — daí morar aqui e não dentro
     * deles. O spinner de cada botão é local do filho que o renderiza.
     */
    const [imprimindo, setImprimindo] = useState<'campo' | 'laboratorio' | null>(null)

    // "Laboratório" só faz sentido depois da coleta realizada em campo, então é liberada
    // para qualquer status após `COLETADA`.
    const isLaboratorioLiberado: boolean = !([1, 2].includes(status));

    function fecharPopover() {
        setAbrirPopover(false)
    }

    return (
        <>
            <Popover
                trigger='click'
                open={abrirPopover}
                // Enquanto imprime, o popover não fecha: ele ancora o spinner do botão
                // clicado, e um clique fora tiraria da tela o único sinal de progresso.
                // Quem o fecha é o filho, depois de abrir a gaveta.
                onOpenChange={(aberto) => { if (!imprimindo) setAbrirPopover(aberto) }}
                content={() => (
                    // A linha da tabela abre a edição no duplo clique. A coluna "Ações"
                    // já é ignorada por lá, mas o popover/gaveta são renderizados em
                    // portal — barrar aqui também garante o comportamento independente
                    // de onde o portal cair no DOM. O <div> existe só para isso: o
                    // <Box> é compartilhado e não repassa handlers de evento.
                    <div onDoubleClick={(e) => e.stopPropagation()}>
                        <Box style={{ alignItems: 'center' }}>
                            <TituloPopover>Tipo de ordem de coleta</TituloPopover>

                            {/* Cada ordem traz o próprio botão: aqui só definimos a
                                posição no popover e a coordenação entre as duas. */}
                            <OrdemCampo
                                cod={cod}
                                fecharPopover={fecharPopover}
                                onImprimindo={(ativo) => setImprimindo(ativo ? 'campo' : null)}
                                bloqueado={imprimindo === 'laboratorio'}
                            />

                            <OrdemLaboratorio
                                cod={cod}
                                fecharPopover={fecharPopover}
                                onImprimindo={(ativo) => setImprimindo(ativo ? 'laboratorio' : null)}
                                bloqueado={imprimindo === 'campo'}
                                liberado={isLaboratorioLiberado}
                            />
                        </Box>
                    </div>
                )}
            >
                <Tooltip placement='bottom' title='Imprimir ordem de coleta'>
                    <span
                        style={{ display: 'inline-flex' }}
                        onDoubleClick={(e) => e.stopPropagation()}
                    >
                        {/* Sem `loading` aqui: o progresso é mostrado no botão do
                            popover que foi clicado, que é onde o usuário está olhando. */}
                        <ButtonCustom
                            icon={<FontAwesomeIcon icon={faPrint} />}
                            type='secondary'
                            onClick={undefined}
                        />
                    </span>
                </Tooltip>
            </Popover>
        </>
    )
}
